import React, { useContext, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import MISReportPrintDocument from "./MISReportPrintDocument";
import { exportMisReportPdf } from "./exportMisReportPdf";
import { BREAKUP_LABELS, money, num } from "./misReportShared";
import DashboardBranchFilter, {
  useDashboardBranch,
} from "../Includes/DashboardBranchFilter";
import "../AdminDashboard/AdminDashboard.css";
import "./MISReport.css";

const BRAND = "#e8002b";
const PIE = [BRAND, "#0f766e", "#d97706", "#2563eb", "#7c3aed"];

const Kpi = ({ label, value, sub, tone = "slate", icon }) => (
  <div className={`ad-kpi ad-tone-${tone}`}>
    <div className="ad-kpi-body">
      <p className="ad-kpi-label">{label}</p>
      <h4 className="ad-kpi-value">{value}</h4>
      {sub ? <p className="ad-kpi-sub">{sub}</p> : null}
    </div>
    <div className="ad-kpi-icon">
      <i className={`bi ${icon}`} />
    </div>
  </div>
);

const SalesDetailTable = ({ title, rows, totalRow }) => (
  <div className="ad-panel">
    <div className="ad-panel-head">
      <h5>{title}</h5>
    </div>
    <div className="table-responsive mis-table-wrap">
      <table className="table table-sm mis-table mis-table-wide mb-0">
        <thead>
          <tr>
            <th>Particulars</th>
            <th className="text-end">Qty</th>
            <th className="text-end">Price</th>
            <th className="text-end">Discount</th>
            <th className="text-end">GST</th>
            <th className="text-end">Net</th>
          </tr>
        </thead>
        <tbody>
          {rows}
          {totalRow}
        </tbody>
      </table>
    </div>
  </div>
);

const branchLabelFromId = (id) =>
  id === "1" ? "G-13" : id === "15" ? "F-11" : "All Branches";

export default function MISReport() {
  const { state } = useContext(NoteState);
  const { role, branch_id } = state;
  const { branch, setBranch } = useDashboardBranch(role, branch_id);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [generatedFor, setGeneratedFor] = useState(null);
  const printRef = useRef(null);

  const filtersDirty =
    data &&
    generatedFor &&
    (generatedFor.branch !== branch || generatedFor.date !== date);

  const generateReport = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await UserServices.MISReport(branch, { date, timeout: 30000 });
      const payload = res?.data?.data ?? res?.data;
      if (payload?.meta) {
        setData(payload);
        setGeneratedFor({ branch, date });
      } else {
        setData(null);
        setGeneratedFor(null);
        setError("Could not load MIS report.");
      }
    } catch (e) {
      console.error(e);
      setData(null);
      setGeneratedFor(null);
      setError("Could not load MIS report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const exec = data?.executive || {};
  const breakup = data?.sales?.breakup_today || {};
  const foot = data?.footfall || {};
  const hr = data?.hr || {};
  const dept = data?.departments || {};
  const today = data?.sales?.today || {};
  const mtd = data?.sales?.mtd || {};

  const breakupChart = useMemo(
    () =>
      Object.entries(BREAKUP_LABELS)
        .map(([key, name]) => ({
          name: name.replace("Personal Training ", "PT "),
          net: Number(breakup[key]?.net || 0),
          qty: Number(breakup[key]?.qty || 0),
        }))
        .filter((r) => r.net > 0),
    [breakup]
  );

  const footfallPie = useMemo(
    () => [
      { name: "Morning", value: Number(foot.morning || 0) },
      { name: "Afternoon", value: Number(foot.afternoon || 0) },
      { name: "Evening", value: Number(foot.evening || 0) },
    ],
    [foot]
  );

  const trend = data?.trend || [];
  const trainersToday = data?.trainers?.top_today || [];
  const trainersMtd = data?.trainers?.top_mtd || [];
  const activeAsOfLabel = data?.meta?.date ? moment(data.meta.date).format("DD MMM YYYY") : "";
  const oldest = data?.membership?.oldest_active_packages || [];
  const byBranch = data?.by_branch || [];
  const social = dept?.social_leads || {};
  const fy = data?.fiscal_year || {};
  const fyMonthly = fy.monthly || [];
  const fyServicePackages = fy.top_service_packages || fy.top_packages || [];
  const fyCafeProducts = fy.top_cafe_products || [];
  const physioDetail = dept?.physio_detail || {};
  const nutritionDetail = dept?.nutrition_detail || {};
  const lateStaff = hr?.late_staff || [];
  const absentStaff = hr?.absent_staff || [];
  const leaveStaff = hr?.leave_staff || [];
  const topLateMonth = hr?.top_late_month || [];

  const deptOverview = useMemo(() => {
    if (!data) return [];
    const gymNet = Number(breakup.gym_new?.net || 0) + Number(breakup.gym_renew?.net || 0);
    const gymQty = Number(breakup.gym_new?.qty || 0) + Number(breakup.gym_renew?.qty || 0);
    const ptNet = Number(breakup.pt_new?.net || 0) + Number(breakup.pt_renew?.net || 0);
    const ptQty = Number(breakup.pt_new?.qty || 0) + Number(breakup.pt_renew?.qty || 0);
    return [
      {
        name: "Gym membership",
        qty: gymQty,
        net: gymNet,
        note: `${exec.active_gym_members ?? 0} gym members active${activeAsOfLabel ? ` as of ${activeAsOfLabel}` : ""}`,
      },
      { name: "Personal training", qty: ptQty, net: ptNet, note: `${hr.pt_staff_present ?? 0} trainers present` },
      {
        name: "GX Studio",
        qty: breakup.gx?.qty,
        net: breakup.gx?.net,
        note: `${foot.gx_active_members ?? 0} active · ${foot.gx_present ?? 0} present / ${foot.gx_absent ?? 0} absent`,
      },
      {
        name: "Academy",
        qty: breakup.academy?.qty,
        net: breakup.academy?.net,
        note: `${foot.academy_active_members ?? 0} active members`,
      },
      {
        name: "Nutrition",
        qty: breakup.nutrition?.qty,
        net: breakup.nutrition?.net,
        note: `${nutritionDetail.consultations_today ?? dept.nutrition?.appointments_today ?? 0} consults · ${nutritionDetail.meal_plans_issued_mtd ?? 0} meal plans MTD`,
      },
      {
        name: "Physiotherapy",
        qty: breakup.physio?.qty,
        net: breakup.physio?.net,
        note: `${physioDetail.consultations_conducted_today ?? 0} conducted · ${physioDetail.appointments_mtd ?? 0} appts MTD`,
      },
      { name: "Café", qty: breakup.cafe?.qty, net: breakup.cafe?.net, note: `MTD ${money(exec.cafe_mtd_net)}` },
      { name: "Other", qty: breakup.other?.qty, net: breakup.other?.net, note: "—" },
      { name: "Social / leads", qty: social.leads_today, net: null, note: `${social.payments ?? 0} paid · ${social.visit_completed ?? 0} visits` },
    ];
  }, [data, breakup, exec, foot, hr, dept, social, activeAsOfLabel, physioDetail, nutritionDetail]);

  const downloadPdf = async () => {
    const root = printRef.current?.querySelector(".mis-print-root");
    if (!root) {
      setError("PDF layout not ready. Refresh and try again.");
      return;
    }
    setDownloading(true);
    setError("");
    try {
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 200)));
      await exportMisReportPdf(root, { branch, date });
    } catch (err) {
      console.error(err);
      setError("PDF export failed.");
    } finally {
      setDownloading(false);
    }
  };

  const branchLabel = data?.meta?.branch_label || branchLabelFromId(branch);

  const breakupRows = Object.entries(BREAKUP_LABELS).map(([key, label]) => {
    const row = breakup[key] || {};
    return (
      <tr key={key}>
        <td>{label}</td>
        <td className="text-end">{row.qty ?? 0}</td>
        <td className="text-end">{money(row.price)}</td>
        <td className="text-end">{money(row.discount)}</td>
        <td className="text-end">{money(row.tax)}</td>
        <td className="text-end">{money(row.net)}</td>
      </tr>
    );
  });

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell mis-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="mis-page-header">
              <div className="mis-page-header-text">
                <p className="ad-eyebrow">Administration</p>
                <h2 className="ad-title">
                  <i className="bi bi-file-earmark-bar-graph me-2" />
                  MIS Report
                </h2>
                <p className="ad-subtitle">
                  {data ? (
                    <>
                      {branchLabel} · {moment(data.meta.date).format("dddd, DD MMM YYYY")}
                      <span className="mis-source-note"> · Sales = Detailed Sales Report (price + tax)</span>
                    </>
                  ) : (
                    "Choose branch and date, then click Generate Report."
                  )}
                </p>
              </div>
            </div>

            <div className="mis-filter-bar">
              <div className="mis-filter-field">
                <label htmlFor="mis-branch">Branch</label>
                <DashboardBranchFilter
                  id="mis-branch"
                  role={role}
                  loginBranchId={branch_id}
                  value={branch}
                  onChange={setBranch}
                />
              </div>
              <div className="mis-filter-field">
                <label htmlFor="mis-date">Date</label>
                <input
                  id="mis-date"
                  type="date"
                  className="form-control"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="mis-filter-actions">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={generateReport}
                  disabled={loading}
                >
                  {loading ? "Generating…" : data ? "Regenerate" : "Generate Report"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={downloadPdf}
                  disabled={!data || downloading || loading}
                >
                  {downloading ? "Building PDF…" : "Download PDF"}
                </button>
                <Link to="/detailed-sales-report" className="btn btn-outline-secondary">
                  Detailed Sales
                </Link>
              </div>
            </div>

            {error ? <div className="alert alert-warning py-2">{error}</div> : null}
            {filtersDirty ? (
              <div className="alert alert-info py-2 mb-0">
                Branch or date changed — click <strong>Regenerate</strong> to refresh the report.
              </div>
            ) : null}

            {!data && !loading ? (
              <div className="mis-empty-state">
                <i className="bi bi-calendar3 mis-empty-icon" aria-hidden="true" />
                <p>No report generated yet.</p>
                <p className="text-muted small">Select filters above and click Generate Report.</p>
              </div>
            ) : null}

            {loading && !data ? (
              <div className="mis-loading-block text-center py-5">
                <div className="spinner-border text-danger" role="status" />
                <div className="text-muted mt-2 small">Building MIS report…</div>
              </div>
            ) : null}

            {data ? (
              <div className={`mis-report-body${loading ? " mis-report-busy" : ""}`}>
                {loading ? (
                  <div className="mis-report-busy-overlay">
                    <div className="spinner-border text-danger spinner-border-sm" role="status" />
                    <span>Updating…</span>
                  </div>
                ) : null}
                <h3 className="ad-section-title">Executive summary</h3>
                <div className="ad-kpi-grid">
                  <Kpi
                    label="Today sales (net)"
                    value={money(exec.sales_today_net)}
                    sub={`Services ${money(exec.services_today_net)} · Café ${money(exec.cafe_today_net)}`}
                    icon="bi-cash-stack"
                    tone="red"
                  />
                  <Kpi
                    label="Month sales (net)"
                    value={money(exec.sales_mtd_net)}
                    sub={`${moment(data.meta.month_start).format("DD MMM")} – ${moment(data.meta.date).format("DD MMM")}`}
                    icon="bi-graph-up-arrow"
                    tone="slate"
                  />
                  <Kpi
                    label={activeAsOfLabel ? `Active clients (as of ${activeAsOfLabel})` : "Active clients"}
                    value={num(exec.active_clients)}
                    sub={`Membership (reg.) + active gym · ${num(exec.active_packages)} package lines · Gym-only ${num(exec.active_gym_members)}`}
                    icon="bi-people"
                    tone="teal"
                  />
                  <Kpi
                    label="Footfall"
                    value={num(exec.footfall_checkins)}
                    sub={`M ${num(foot.total_males)} · F ${num(foot.total_females)} · Absent paid ${num(foot.absent_paid_gym)}`}
                    icon="bi-door-open"
                    tone="slate"
                  />
                  <Kpi
                    label="Staff present"
                    value={num(exec.staff_present)}
                    sub={`of ${num(exec.staff_total)} · PT ${num(hr.pt_staff_present)}`}
                    icon="bi-person-check"
                    tone="red"
                  />
                  <Kpi
                    label="Today profit"
                    value={money(exec.profit_today)}
                    sub={`Expenses ${money(exec.expenses_today)} · MTD exp ${money(exec.expenses_mtd)}`}
                    icon="bi-piggy-bank"
                    tone="amber"
                  />
                </div>

                <h3 className="ad-section-title">All departments — overview</h3>
                <div className="ad-panel mb-4">
                  <div className="table-responsive mis-table-wrap">
                    <table className="table table-sm mis-table mb-0">
                      <thead>
                        <tr>
                          <th>Department</th>
                          <th className="text-end">Today qty</th>
                          <th className="text-end">Today net</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deptOverview.map((row) => (
                          <tr key={row.name}>
                            <td>
                              <strong>{row.name}</strong>
                            </td>
                            <td className="text-end">{num(row.qty)}</td>
                            <td className="text-end">{row.net == null ? "—" : money(row.net)}</td>
                            <td className="text-muted small">{row.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {data.meta.is_all_branches && byBranch.length ? (
                  <>
                    <h3 className="ad-section-title">Branch comparison</h3>
                    <div className="table-responsive mb-4">
                      <table className="table table-sm mis-table">
                        <thead>
                          <tr>
                            <th>Branch</th>
                            <th className="text-end">Today sales</th>
                            <th className="text-end">Footfall</th>
                            <th className="text-end">Staff present</th>
                            <th className="text-end">Physio</th>
                            <th className="text-end">Nutrition</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byBranch.map((b) => (
                            <tr key={b.branch_id}>
                              <td>{b.branch_label}</td>
                              <td className="text-end">{money(b.total_sales_today)}</td>
                              <td className="text-end">{num(b.totalCheckins)}</td>
                              <td className="text-end">{num(b.presentStaff)}</td>
                              <td className="text-end">{money(b.physio_sales)}</td>
                              <td className="text-end">{money(b.nutrition_sales)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : null}

                <h3 className="ad-section-title">Finance</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="mis-finance-card">
                      <span>MTD sales (net)</span>
                      <strong>{money(exec.sales_mtd_net)}</strong>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="mis-finance-card">
                      <span>MTD expenses</span>
                      <strong>{money(exec.expenses_mtd)}</strong>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="mis-finance-card">
                      <span>Today profit</span>
                      <strong>{money(exec.profit_today)}</strong>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="mis-finance-card">
                      <span>Pending expense approvals</span>
                      <strong>{num(exec.pending_expense_approvals)}</strong>
                    </div>
                  </div>
                </div>

                <div className="mis-fy-panel mb-4">
                  <div className="mis-fy-panel-head">
                    <div>
                      <h3 className="mis-fy-title">Fiscal year performance</h3>
                      <p className="mis-fy-sub">
                        {fy.label || "Fiscal year"} · {fy.period_label || "—"}
                        {fy.fiscal_year_start_month
                          ? ` · FY starts month ${fy.fiscal_year_start_month} (Finance settings)`
                          : null}
                      </p>
                    </div>
                    <div className="mis-fy-totals">
                      <div>
                        <span>Sales (net)</span>
                        <strong>{money(fy.totals?.sales_net)}</strong>
                      </div>
                      <div>
                        <span>Expenses</span>
                        <strong>{money(fy.totals?.expenses)}</strong>
                      </div>
                      <div>
                        <span>Profit</span>
                        <strong>{money(fy.totals?.profit)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-xl-7">
                      <div className="ad-panel">
                        <div className="ad-panel-head">
                          <h5>Month-wise sales &amp; expenses</h5>
                        </div>
                        <div className="ad-chart">
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={fyMonthly} margin={{ left: 4, right: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} width={56} />
                              <Tooltip formatter={(v) => money(v)} />
                              <Legend />
                              <Bar dataKey="sales_net" name="Sales net" fill="#2563eb" radius={[3, 3, 0, 0]} />
                              <Bar dataKey="expenses" name="Expenses" fill="#d97706" radius={[3, 3, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="table-responsive mis-table-wrap">
                          <table className="table table-sm mis-table mb-0">
                            <thead>
                              <tr>
                                <th>Month</th>
                                <th className="text-end">Qty</th>
                                <th className="text-end">Sales net</th>
                                <th className="text-end">Expenses</th>
                                <th className="text-end">Profit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fyMonthly.map((m) => (
                                <tr key={m.key}>
                                  <td>
                                    {m.label}
                                    {m.is_partial ? " *" : ""}
                                  </td>
                                  <td className="text-end">{num(m.sales_qty)}</td>
                                  <td className="text-end">{money(m.sales_net)}</td>
                                  <td className="text-end">{money(m.expenses)}</td>
                                  <td className="text-end">{money(m.profit)}</td>
                                </tr>
                              ))}
                              <tr className="mis-total-row">
                                <td>
                                  <strong>Total</strong>
                                </td>
                                <td className="text-end">
                                  <strong>{num(fy.totals?.sales_qty)}</strong>
                                </td>
                                <td className="text-end">
                                  <strong>{money(fy.totals?.sales_net)}</strong>
                                </td>
                                <td className="text-end">
                                  <strong>{money(fy.totals?.expenses)}</strong>
                                </td>
                                <td className="text-end">
                                  <strong>{money(fy.totals?.profit)}</strong>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-xl-5">
                      <div className="ad-panel h-100">
                        <div className="ad-panel-head">
                          <h5>Top services &amp; café (fiscal period)</h5>
                        </div>
                        <div className="row g-0">
                          <div className="col-12 col-lg-6 border-end">
                            <p className="small fw-semibold text-muted px-3 pt-2 mb-1">Gym &amp; services</p>
                            <div className="table-responsive mis-table-wrap">
                              <table className="table table-sm mis-table mis-table-compact mb-0">
                                <thead>
                                  <tr>
                                    <th>Package</th>
                                    <th className="text-end">Qty</th>
                                    <th className="text-end">Net</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fyServicePackages.length ? (
                                    fyServicePackages.map((p) => (
                                      <tr key={`svc-${p.package_id}`}>
                                        <td className="mis-cell-package" title={p.package_name}>
                                          {p.package_name}
                                        </td>
                                        <td className="text-end">{p.qty}</td>
                                        <td className="text-end mis-cell-money">{money(p.net)}</td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={3} className="text-muted">
                                        No service sales
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div className="col-12 col-lg-6">
                            <p className="small fw-semibold text-muted px-3 pt-2 mb-1">Café products</p>
                            <div className="table-responsive mis-table-wrap">
                              <table className="table table-sm mis-table mis-table-compact mb-0">
                                <thead>
                                  <tr>
                                    <th>Product</th>
                                    <th className="text-end">Qty</th>
                                    <th className="text-end">Net</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fyCafeProducts.length ? (
                                    fyCafeProducts.map((p) => (
                                      <tr key={`cafe-${p.package_id}`}>
                                        <td className="mis-cell-package" title={p.package_name}>
                                          {p.package_name}
                                        </td>
                                        <td className="text-end">{p.qty}</td>
                                        <td className="text-end mis-cell-money">{money(p.net)}</td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={3} className="text-muted">
                                        No café sales
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="ad-section-title">Sales (report date)</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-6">
                    <SalesDetailTable
                      title="Sales summary"
                      rows={
                        <>
                          <tr>
                            <td>Sales till date (month start → report date)</td>
                            <td className="text-end">{mtd.qty ?? 0}</td>
                            <td className="text-end">{money(mtd.price)}</td>
                            <td className="text-end">{money(mtd.discount)}</td>
                            <td className="text-end">{money(mtd.tax)}</td>
                            <td className="text-end">{money(mtd.net)}</td>
                          </tr>
                          <tr className="mis-total-row">
                            <td>
                              <strong>Today&apos;s total sale</strong>
                            </td>
                            <td className="text-end">
                              <strong>{today.qty ?? 0}</strong>
                            </td>
                            <td className="text-end">
                              <strong>{money(today.price)}</strong>
                            </td>
                            <td className="text-end">
                              <strong>{money(today.discount)}</strong>
                            </td>
                            <td className="text-end">
                              <strong>{money(today.tax)}</strong>
                            </td>
                            <td className="text-end">
                              <strong>{money(today.net)}</strong>
                            </td>
                          </tr>
                        </>
                      }
                    />
                  </div>
                  <div className="col-12 col-xl-6">
                    <SalesDetailTable
                      title="Daily sales breakup"
                      rows={breakupRows}
                      totalRow={
                        <tr className="mis-total-row">
                          <td>
                            <strong>Total</strong>
                          </td>
                          <td className="text-end">
                            <strong>{today.qty ?? 0}</strong>
                          </td>
                          <td className="text-end">
                            <strong>{money(today.price)}</strong>
                          </td>
                          <td className="text-end">
                            <strong>{money(today.discount)}</strong>
                          </td>
                          <td className="text-end">
                            <strong>{money(today.tax)}</strong>
                          </td>
                          <td className="text-end">
                            <strong>{money(today.net)}</strong>
                          </td>
                        </tr>
                      }
                    />
                  </div>
                </div>

                <h3 className="ad-section-title">Charts</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-7">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Sales vs expenses (7 days)</h5>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={260}>
                          <LineChart data={trend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} width={56} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Legend />
                            <Line type="monotone" dataKey="sales" name="Sales" stroke={BRAND} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#d97706" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-xl-5">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Footfall by time</h5>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie data={footfallPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78}>
                              {footfallPie.map((_, i) => (
                                <Cell key={i} fill={PIE[i % PIE.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <p className="mis-footnote px-3 pb-2">
                        Busiest: {foot.busiest_slot || "—"} · Slowest: {foot.slowest_slot || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Today sales breakup (net)</h5>
                        <Link to="/detailed-sales-report" className="ad-chip">
                          Verify in Detailed Sales
                        </Link>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={breakupChart} margin={{ left: 8, right: 16 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                            <YAxis tick={{ fontSize: 11 }} width={56} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Bar dataKey="net" fill={BRAND} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="ad-section-title">Physio &amp; nutrition (detail)</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-lg-6">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Physiotherapy</h5>
                      </div>
                      <ul className="mis-dl">
                        <li>
                          <span>Appointments today / MTD</span>
                          <strong>
                            {num(physioDetail.appointments_today)} / {num(physioDetail.appointments_mtd)}
                          </strong>
                        </li>
                        <li>
                          <span>Consultations conducted today / MTD</span>
                          <strong>
                            {num(physioDetail.consultations_conducted_today)} /{" "}
                            {num(physioDetail.consultations_conducted_mtd)}
                          </strong>
                        </li>
                        <li>
                          <span>Prescriptions MTD · Referrals MTD</span>
                          <strong>
                            {num(physioDetail.prescriptions_mtd)} · {num(physioDetail.referrals_mtd)}
                          </strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Nutrition</h5>
                      </div>
                      <ul className="mis-dl">
                        <li>
                          <span>Appointments today / MTD</span>
                          <strong>
                            {num(nutritionDetail.appointments_today)} / {num(nutritionDetail.appointments_mtd)}
                          </strong>
                        </li>
                        <li>
                          <span>Consultations logged today / MTD</span>
                          <strong>
                            {num(nutritionDetail.consultations_today)} / {num(nutritionDetail.consultations_mtd)}
                          </strong>
                        </li>
                        <li>
                          <span>Meal plans issued today / MTD · Conversions MTD</span>
                          <strong>
                            {num(nutritionDetail.meal_plans_issued_today)} / {num(nutritionDetail.meal_plans_issued_mtd)} ·{" "}
                            {num(nutritionDetail.conversions_mtd)}
                          </strong>
                        </li>
                        <li>
                          <span>Health camps MTD · Total meal plans</span>
                          <strong>
                            {num(nutritionDetail.health_camps_mtd)} · {num(nutritionDetail.meal_plans_total)}
                          </strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <h3 className="ad-section-title">Footfall, café &amp; departments</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-lg-4">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Footfall</h5>
                      </div>
                      <ul className="mis-dl">
                        <li>
                          <span>Morning / Afternoon / Evening</span>
                          <strong>
                            {num(foot.morning)} / {num(foot.afternoon)} / {num(foot.evening)}
                          </strong>
                        </li>
                        <li>
                          <span>Check-ins · M / F</span>
                          <strong>
                            {num(foot.total_checkins)} · {num(foot.total_males)}/{num(foot.total_females)}
                          </strong>
                        </li>
                        <li>
                          <span>
                            Active gym · Absent paid
                            {activeAsOfLabel ? ` (${activeAsOfLabel})` : ""}
                          </span>
                          <strong>
                            {num(foot.active_gym_members)} · {num(foot.absent_paid_gym)}
                          </strong>
                        </li>
                        <li>
                          <span>GX / Vitality (active · present · absent)</span>
                          <strong>
                            {num(foot.gx_active_members)} · {num(foot.gx_present)} · {num(foot.gx_absent)}
                          </strong>
                        </li>
                        <li>
                          <span>Academy active · Studio check-ins · Walk-ins</span>
                          <strong>
                            {num(foot.academy_active_members)} · {num(foot.studio_total)} · {num(foot.visitors_walkins)}
                          </strong>
                        </li>
                        {(foot.studio_slots || []).map((s) => (
                          <li key={s.slot}>
                            <span>Slot {s.slot}</span>
                            <strong>{s.count}</strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Café</h5>
                      </div>
                      <ul className="mis-dl">
                        <li>
                          <span>Net sales</span>
                          <strong>{money(data.cafe?.net_today)}</strong>
                        </li>
                        <li>
                          <span>Meals / Drinks / Sides</span>
                          <strong>
                            {num(data.cafe?.meals)} / {num(data.cafe?.drinks)} / {num(data.cafe?.sides)}
                          </strong>
                        </li>
                        <li>
                          <span>Staff orders</span>
                          <strong>{num(data.cafe?.staff_orders)}</strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Departments</h5>
                      </div>
                      <ul className="mis-dl">
                        <li>
                          <span>Physio (today / MTD)</span>
                          <strong>
                            {money(dept.physio?.sales_net)} / {money(dept.physio?.mtd_net)}
                          </strong>
                        </li>
                        <li>
                          <span>Nutrition (today / MTD)</span>
                          <strong>
                            {money(dept.nutrition?.sales_net)} / {money(dept.nutrition?.mtd_net)}
                          </strong>
                        </li>
                        <li>
                          <span>Appts physio / nutrition</span>
                          <strong>
                            {num(dept.physio?.appointments_today)} / {num(dept.nutrition?.appointments_today)}
                          </strong>
                        </li>
                        <li>
                          <span>Social leads / paid</span>
                          <strong>
                            {num(dept.social_leads?.leads_today)} / {num(dept.social_leads?.payments)}
                          </strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <h3 className="ad-section-title">HR, PT &amp; trainers</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-lg-3">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>HR</h5>
                      </div>
                      <ul className="mis-dl">
                        <li>
                          <span>Present / Total</span>
                          <strong>
                            {num(hr.present)} / {num(hr.total_staff)}
                          </strong>
                        </li>
                        <li>
                          <span>Absent / Late / Leave</span>
                          <strong>
                            {num(hr.absent)} / {num(hr.late)} / {num(hr.leave_count)}
                          </strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-12 col-lg-3">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Personal training</h5>
                      </div>
                      <ul className="mis-dl">
                        <li>
                          <span>PT staff present</span>
                          <strong>{num(hr.pt_staff_present)}</strong>
                        </li>
                        <li>
                          <span>PT new / renew (qty)</span>
                          <strong>
                            {breakup.pt_new?.qty ?? 0} / {breakup.pt_renew?.qty ?? 0}
                          </strong>
                        </li>
                        <li>
                          <span>Physio / Nutrition qty</span>
                          <strong>
                            {breakup.physio?.qty ?? 0} / {breakup.nutrition?.qty ?? 0}
                          </strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-12 col-lg-3">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Top PT — today</h5>
                      </div>
                      <div className="mis-table-wrap px-2 pb-2">
                        <table className="table table-sm mis-table mb-0">
                          <tbody>
                            {trainersToday.slice(0, 5).map((t) => (
                              <tr key={t.trainer_id}>
                                <td>{t.trainer_name}</td>
                                <td className="text-end">{money(t.net)}</td>
                              </tr>
                            ))}
                            {!trainersToday.length ? (
                              <tr>
                                <td className="text-muted">No sales</td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-3">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Top PT — month</h5>
                      </div>
                      <div className="mis-table-wrap px-2 pb-2">
                        <table className="table table-sm mis-table mb-0">
                          <tbody>
                            {trainersMtd.slice(0, 5).map((t) => (
                              <tr key={t.trainer_id}>
                                <td>{t.trainer_name}</td>
                                <td className="text-end">{money(t.net)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12 col-lg-6">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Longest-running active packages</h5>
                        <Link to="/active-clients-report" className="ad-chip">
                          Active Clients
                        </Link>
                      </div>
                      <div className="table-responsive mis-table-wrap">
                        <table className="table table-sm mis-table mb-0">
                          <thead>
                            <tr>
                              <th>Client</th>
                              <th>Package</th>
                              <th>Started</th>
                            </tr>
                          </thead>
                          <tbody>
                            {oldest.map((row) => (
                              <tr key={row.order_detail_id}>
                                <td>{row.client_name}</td>
                                <td>{row.package_name}</td>
                                <td>{row.start_date ? moment(row.start_date).format("DD MMM YYYY") : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Staff attendance — late, absent &amp; leave (report date)</h5>
                        <span className="text-muted small">
                          Late rule matches HR dashboard (duty start + 15 min)
                        </span>
                      </div>
                      <div className="row g-3 px-2 pb-3">
                        <div className="col-12 col-lg-4">
                          <h6 className="small fw-semibold">Late ({lateStaff.length})</h6>
                          <div className="table-responsive mis-table-wrap mis-table-scroll">
                            <table className="table table-sm mis-table mb-0">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Late by</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lateStaff.map((row) => (
                                  <tr key={`late-${row.user_id}`}>
                                    <td>
                                      {row.name}
                                      <div className="text-muted small">{row.department}</div>
                                    </td>
                                    <td>{row.late_text || "Late"}</td>
                                  </tr>
                                ))}
                                {!lateStaff.length ? (
                                  <tr>
                                    <td colSpan={2} className="text-muted">
                                      None
                                    </td>
                                  </tr>
                                ) : null}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="col-12 col-lg-4">
                          <h6 className="small fw-semibold">Absent ({absentStaff.length})</h6>
                          <div className="table-responsive mis-table-wrap mis-table-scroll">
                            <table className="table table-sm mis-table mb-0">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Dept</th>
                                </tr>
                              </thead>
                              <tbody>
                                {absentStaff.map((row) => (
                                  <tr key={`abs-${row.user_id}`}>
                                    <td>{row.name}</td>
                                    <td>{row.department}</td>
                                  </tr>
                                ))}
                                {!absentStaff.length ? (
                                  <tr>
                                    <td colSpan={2} className="text-muted">
                                      None
                                    </td>
                                  </tr>
                                ) : null}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="col-12 col-lg-4">
                          <h6 className="small fw-semibold">On leave ({leaveStaff.length})</h6>
                          <div className="table-responsive mis-table-wrap mis-table-scroll">
                            <table className="table table-sm mis-table mb-0">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Type</th>
                                </tr>
                              </thead>
                              <tbody>
                                {leaveStaff.map((row, i) => (
                                  <tr key={`lv-${row.user_id}-${i}`}>
                                    <td>{row.name}</td>
                                    <td>{row.leave_type || "Leave"}</td>
                                  </tr>
                                ))}
                                {!leaveStaff.length ? (
                                  <tr>
                                    <td colSpan={2} className="text-muted">
                                      None
                                    </td>
                                  </tr>
                                ) : null}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="col-12">
                          <h6 className="small fw-semibold">
                            Most late this month ({moment(data.meta.month_start).format("MMM YYYY")})
                          </h6>
                          <div className="table-responsive mis-table-wrap">
                            <table className="table table-sm mis-table mb-0">
                              <thead>
                                <tr>
                                  <th>Staff</th>
                                  <th>Department</th>
                                  <th className="text-end">Late days</th>
                                </tr>
                              </thead>
                              <tbody>
                                {topLateMonth.map((row) => (
                                  <tr key={`tl-${row.user_id}`}>
                                    <td>{row.name}</td>
                                    <td>{row.department}</td>
                                    <td className="text-end">{row.late_days}</td>
                                  </tr>
                                ))}
                                {!topLateMonth.length ? (
                                  <tr>
                                    <td colSpan={3} className="text-muted">
                                      No late marks this month
                                    </td>
                                  </tr>
                                ) : null}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="mis-generated">
                  On-screen view · PDF download uses a separate 5-page owner report with full tables and charts
                </p>
              </div>
            ) : null}

            {data ? (
              <div ref={printRef}>
                <MISReportPrintDocument data={data} />
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}
